<template>
  <div class="card mb-4">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h6 class="mb-0">Pricing Preview: {{ rule.insuranceName || `Insurer ${rule.insurance}` }} with {{ rule.percent }}% discount</h6>
      <button type="button" class="btn-close" @click="$emit('close')"></button>
    </div>
    <div class="card-body">
      <div v-if="!isShop" class="mb-3 col-12 col-lg-6">
        <label class="form-label custom-label mb-0">Shop</label>
        <Multiselect
          v-model="shop"
          class="form-control custom-multiselect"
          placeholder="Search a shop under this firm"
          :close-on-select="true"
          :filter-results="false"
          :min-chars="1"
          :resolve-on-load="false"
          :delay="400"
          :searchable="true"
          :options="async (query) => await fetchShops(query)"
          label="firmName"
          value-prop="firm"
          :disabled="loading.preview"
          @change="onShopSelect"
        >
          <template #option="{ option }">
            {{ option.firmName }}
            <span class="badge bg-light text-muted border ms-1">{{ option.type }}</span>
          </template>
          <template #singlelabel="{ value }">
            <div class="multiselect-single-label">
              {{ value.firmName }}
              <span class="badge bg-light text-muted border ms-1">{{ value.type }}</span>
            </div>
          </template>
        </Multiselect>
      </div>

      <div v-if="!shop" class="alert alert-light border">Select a shop to see its prices with this discount.</div>
      <div v-else-if="loading.preview" class="text-muted mb-3">Loading prices...</div>
      <div v-else-if="!preview.length" class="alert alert-light border">
        No prices this discount can apply to: the shop isn't on this insurer's rates and has no prices of its own for this insurer or "Our Pricing". Its estimates would use provider pricing, which is never discounted.
      </div>
      <template v-else>
        <p class="small mb-2">Using {{ sourceLabel }}. {{ scopeNote }}</p>
        <table class="table table-sm align-middle">
          <thead>
            <tr>
              <th>Calibration</th>
              <th class="text-end">Price</th>
              <th class="text-end">Discount</th>
              <th class="text-end">After Discount</th>              
            </tr>
          </thead>
          <tbody>
            <tr v-for="line of lines" :key="line.calibrationType">
              <td>
                {{ line.calibrationTypeName }}
                <span v-if="rule.scope === 'additional' && line.rank === 1" class="badge bg-light text-muted border ms-1">most expensive</span>
              </td>
              <td class="text-end">{{ money(line.price) }}</td>
              <td class="text-end">{{ money(line.discountAmount) }}</td>
              <td class="text-end">{{ money(line.finalPrice) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="rule.scope === 'additional'" class="text-muted small">
          Shown as if every calibration were on one estimate. On a real estimate, the most expensive line on that estimate is billed at full price.
        </p>
      </template>

      <p class="text-muted small mb-0">
        <i class="fa-solid fa-circle-info me-1"></i>
        When a shop is opted into this insurer's program and the insurer has set rates, those rates are used, and this discount replaces the insurer's own discount.
      </p>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import { pricingRuleStore } from '@/stores/pricingRule'
import Multiselect from '@vueform/multiselect'
import globals from '@/plugins/globals'

// PricingRule_Preview's priceSource values: DiagnosticLines_Get steps 1, 1b and 2.
const SOURCE_LABELS = {
  mandated: "the insurer's mandated rates (this shop is opted into its program)",
  insurer: "the shop's own prices for this insurer",
  ourPricing: 'the shop\'s "Our Pricing" defaults'
}

export default {
  name: 'PricingRulePreview',
  components: { Multiselect },
  props: {
    rule: { type: Object, required: true },
    firm: { type: Number, required: true },
    firmType: { type: String, default: null }
  },
  emits: ['close'],
  data() {
    return {
      shop: null
    }
  },
  computed: {
    ...mapState(pricingRuleStore, ['preview', 'loading']),
    isShop() {
      return this.firmType === 'Shop'
    },
    sourceLabel() {
      const source = this.preview.length ? this.preview[0].priceSource : null
      return SOURCE_LABELS[source] || "the shop's prices"
    },
    scopeNote() {
      return this.rule.scope === 'additional'
        ? 'Only ADAS-L calibrations are discounted, and never the most expensive line on an estimate.'
        : 'Every line is discounted.'
    },
    // Mirrors DiagnosticLines_Get's @discountScope branches:
    //   'all'        - every line
    //   'additional' - ADAS-L lines that are not the most expensive line on the estimate
    // Lines are ranked by price as if every calibration were on one estimate
    // (1 = most expensive, ties by list order), like #rankSet's ROW_NUMBER().
    lines() {
      const rate = (this.rule.percent || 0) / 100
      const ranked = this.preview
        .map((p, index) => ({ price: p.price || 0, index }))
        .sort((a, b) => b.price - a.price || a.index - b.index)
      const rankOf = new Map(ranked.map((p, i) => [p.index, i + 1]))
      
      return this.preview.map((p, index) => {
        const price = p.price || 0
        const rank = rankOf.get(index)
        const isDiscounted =
          this.rule.scope === 'all' ||
          (this.rule.scope === 'additional' &&
            rank > 1 &&
            (p.calibrationTypeName || '').toUpperCase().includes('ADAS L'))
        const discountAmount = isDiscounted ? (price * rate) : 0
        const finalPrice = isDiscounted ? price - discountAmount : price
        return { ...p, price, rank, isDiscounted, discountAmount, finalPrice: finalPrice }
      })
    }
  },
  watch: {
    rule() {
      this.load()
    }
  },
  created() {
    this.load()
  },
  methods: {
    ...mapActions(locationStore, ['searchLocations']),
    ...mapActions(pricingRuleStore, ['getPreview', 'clearPreview']),
    async load() {
      if (this.isShop) {
        this.shop = this.firm
      }
      if (!this.shop) {
        this.clearPreview()
        return
      }
      await this.getPreview(this.shop, this.rule.insurance)
    },
    async fetchShops(query) {
      const results = await this.searchLocations({ searchterm: query, firm: this.firm })
      return Array.isArray(results) ? results : []
    },
    async onShopSelect(shop) {
      this.shop = shop
      await this.load()
    },
    money(value) {
      return `$${globals.formatMoney(value)}`
    }
  }
}
</script>
